import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'
import { buildCookie } from '@/modules/cdn/cookie'

import { ApiTaCt100 } from '~/endpoints/ApiTaCt100'
import { ApiTaCt060 } from '~/endpoints/ApiTaCt060'
import { ApiTaCt070 } from '~/endpoints/ApiTaCt070'

// State types

// ApiTaCt100
interface AkamaiToken {
  akamaiToken: string
}
const getEmptyAkamaiToken = (): AkamaiToken => ({
  akamaiToken: '',
})

// ApiTaCt060
interface UnitQuestions {
  contentUrl: string
}
const getEmptyUnitQuestions = (): UnitQuestions => ({
  contentUrl: '',
})

// ApiTaCt070
interface UnitAnswers {
  answerPdfFile: ArrayBuffer | null // PDFのバイナリデータ
}
const getEmptyUnitAnswers = (): UnitAnswers => ({
  answerPdfFile: null,
})

interface UnitAnswersError {
  error: {
    messageId: string
    message: string
  }
  status: number
}
const getEmptyUnitAnswersError = (): UnitAnswersError => ({
  error: {
    messageId: '',
    message: '',
  },
  status: 0,
})
const isUnitAnswersError = (arg: any): arg is UnitAnswersError => {
  return arg.error !== undefined && arg.status !== undefined
}

// State action parameter
interface ActionParamUnitQuestions {
  answerCommentaryId: string
}

interface ActionParamUnitAnswers {
  unitId: string
  subunitId: string
  pieceId: string
}

type ActionParam = ActionParamUnitQuestions | ActionParamUnitAnswers

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/Answer',
})
export default class Answer extends VuexModule {
  // States
  private akamaiToken_ = getEmptyAkamaiToken()

  private unitQuestions_ = getEmptyUnitQuestions()

  private unitAnswers_ = getEmptyUnitAnswers()

  private unitAnswersError_ = getEmptyUnitAnswersError()

  // Getters

  get akamaiCookie(): (expiresAt?: string) => string {
    return (expiresAt?: string) => buildCookie(this.akamaiToken_.akamaiToken, expiresAt)
  }

  get unitQuestions(): UnitQuestions {
    return this.unitQuestions_
  }

  get unitAnswers(): UnitAnswers {
    return this.unitAnswers_
  }

  get isNoAnswerError(): boolean {
    return this.unitAnswersError_.status === 404 && this.unitAnswersError_.error.messageId === 'MSG-00-00130-I'
  }

  get isOtherServerError(): boolean {
    return this.unitAnswersError_.status === 500 && this.unitAnswersError_.error.messageId === 'MSG-00-00147-E'
  }

  // Mutations
  @Mutation
  updateAkamaiToken(value: AkamaiToken) {
    this.akamaiToken_ = value
  }

  @Mutation
  updateUnitQuestions(value: UnitQuestions) {
    this.unitQuestions_ = value
  }

  @Mutation
  updateUnitAnswers(value: UnitAnswers) {
    this.unitAnswers_ = value
  }

  @Mutation
  updateUnitAnswersError(value: UnitAnswersError) {
    this.unitAnswersError_ = value
  }

  // Actions

  @Action
  clear() {
    this.context.commit('updateAkamaiToken', getEmptyAkamaiToken())
    this.context.commit('updateUnitQuestions', getEmptyUnitQuestions())
    this.context.commit('updateUnitAnswers', getEmptyUnitAnswers())
    this.context.commit('updateUnitAnswersError', getEmptyUnitAnswersError())
  }

  @Action
  async getAkamaiToken() {
    logger.trace('in: getAkamaiToken')
    const api = new ApiTaCt100.Api()

    // API: ApiTaCt100
    const result = await api.get<AkamaiToken | null>(
      {},
      // success
      (data, _) => {
        return {
          akamaiToken: data.cmsToken,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateAkamaiToken', result)
  }

  @Action
  async getUnitQuestions(param: ActionParam) {
    logger.trace('in: getUnitQuestions')
    const api = new ApiTaCt060.Api()

    // API: ApiTaCt060
    const result = await api.get<UnitQuestions | null>(
      {
        query: param,
      },
      // success
      (data, _) => {
        return {
          contentUrl: data.contentUrl,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateUnitQuestions', result)
  }

  @Action
  async getUnitAnswers(param: ActionParam) {
    logger.trace('in: getUnitAnswers')
    const api = new ApiTaCt070.Api()

    // API: ApiTaCt070
    const result = await api.get<UnitAnswers | UnitAnswersError>(
      {
        query: param,
      },
      // success
      (data, _) => {
        return {
          answerPdfFile: data,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)

        const jsonStr = Buffer.from(data).toString('utf8')
        const jsonObj = JSON.parse(jsonStr)

        // ApiTaCt070はエラー種別によって場合分けが必要（DEV_EL2ND−603）
        return {
          error: { messageId: jsonObj.error.messageId, message: jsonObj.error.message },
          status,
        }
      }
    )
    if (result === null || result === undefined) {
      return
    }
    if (isUnitAnswersError(result)) {
      this.context.commit('updateUnitAnswersError', result)
      return
    }

    // 今回のリクエストのレスポンスPDFをセットし、以前のエラーをクリアする
    this.context.commit('updateUnitAnswers', result)
    this.context.commit('updateUnitAnswersError', getEmptyUnitAnswersError())
  }
}
